import type { ApiContent } from '@/lib/api/types';
import type { InspirationFilterValue } from '@/components/demand/InspirationFilters';
import type { MarketingAttributes } from '@sizzlenz/shared';
import { MARKETING_TAG_PREFIXES } from '@sizzlenz/shared';

import type { MarketingDetailsValue } from '@/components/content/MarketingDetailsForm';

/** Path of the Inspiration Feed page. */
export const INSPIRATION_FEED_PATH = '/inspiration-feed';

/**
 * Demand facet key -> Inspiration Feed URL query param name.
 *
 * This is the single source of truth for the feed's URL contract and must stay
 * in sync with the `useUrlSync` config in InspirationFeed. Facets that the feed
 * does not filter on (objectives, delivery disciplines) are intentionally
 * absent — chips for those facets are not linkable.
 */
export const DEMAND_FACET_FEED_PARAM: Partial<Record<keyof MarketingDetailsValue, string>> = {
  marketingRoles: 'roles',
  journeyStages: 'stages',
  productStages: 'product',
  tactics: 'tactics',
  channels: 'channels',
  deliveryActivities: 'execution',
  collaborationModels: 'collab',
  activationFormats: 'format',
};

/** True when a given demand facet can be linked through to the Inspiration Feed. */
export function isLinkableDemandFacet(facetKey: keyof MarketingDetailsValue): boolean {
  return facetKey in DEMAND_FACET_FEED_PARAM;
}

/**
 * Build an Inspiration Feed href pre-filtered to a single facet value.
 *
 * Returns `undefined` for facets the feed doesn't filter on, so callers can
 * render a non-clickable chip instead of a dead link.
 */
export function buildInspirationFeedHref(
  facetKey: keyof MarketingDetailsValue,
  value: string
): string | undefined {
  const param = DEMAND_FACET_FEED_PARAM[facetKey];
  if (!param) return undefined;
  return `${INSPIRATION_FEED_PATH}?${param}=${encodeURIComponent(value)}`;
}

/**
 * Inspiration-feed filter facet -> the systemTag prefix used on content records
 * (see MARKETING_TAG_PREFIXES / buildMarketingTags in the API). Used to
 * turn a filter selection into `systemTags` query values for a coarse OR
 * prefilter on the server; exact OR-within/AND-across semantics are then
 * refined client-side via matchesInspirationFilters.
 */
const FILTER_KEY_TAG_PREFIX: Record<keyof InspirationFilterValue, string> = {
  demandRoles: MARKETING_TAG_PREFIXES.marketingRole,
  journeyStages: MARKETING_TAG_PREFIXES.journeyStage,
  productStages: MARKETING_TAG_PREFIXES.productStage,
  demandObjectives: MARKETING_TAG_PREFIXES.marketingObjective,
  tactics: MARKETING_TAG_PREFIXES.tactic,
  channels: MARKETING_TAG_PREFIXES.channel,
  deliveryActivities: MARKETING_TAG_PREFIXES.deliveryActivity,
  collaborationModels: MARKETING_TAG_PREFIXES.collaborationModel,
  activationFormats: MARKETING_TAG_PREFIXES.activationFormat,
};

/**
 * Flatten selected feed filters into namespaced systemTag values
 * (e.g. `demand-role:sales-activation`, `journey:trial`).
 *
 * Returns an empty array when nothing is selected. The API applies these with
 * OR semantics (arrayOverlaps), so this is a coarse candidate prefilter — the
 * feed refines results client-side for exact AND-across-facet behaviour.
 */
export function buildInspirationFilterSystemTags(filters: InspirationFilterValue): string[] {
  const tags: string[] = [];
  for (const key of Object.keys(filters) as (keyof InspirationFilterValue)[]) {
    const prefix = FILTER_KEY_TAG_PREFIX[key];
    for (const value of filters[key]) {
      tags.push(`${prefix}:${value}`);
    }
  }
  return tags;
}

/**
 * Build systemTag groups for the feed, keeping locked facet values as their own
 * mandatory groups — separate from the user's own selection in the same facet.
 *
 * The API OR's values within a group (arrayOverlaps) and AND's groups together.
 * We emit one group per (facet, source) so that:
 * - A locked value is its own group → it is ALWAYS required (AND).
 * - The user's selection for a facet is a separate group → also required when
 *   present, with its own values OR'd within.
 *
 * This is deliberately NOT "locked ∪ user within one facet". Merging them into a
 * single OR group would let the locked value alone satisfy the facet, so a user
 * narrowing (e.g. adding "retail") would have no effect — a record carrying only
 * the locked "collaboration" tactic would still match. Keeping them as separate
 * AND groups means: has(collaboration) AND has(retail).
 *
 * Example — locked `{ tactics: ['collaboration'] }`, user picks tactic `retail`:
 *   [['tactic:collaboration'], ['tactic:retail-display-pos']]
 *   → collaboration AND retail (a collaboration-only record is excluded).
 *
 * Contrast with {@link buildInspirationFilterSystemTags}, which flattens everything
 * into a single OR and cannot express a mandatory facet.
 *
 * @param userFilters   The user's own selection (from the URL/controls).
 * @param lockedFilters Host-configured values that are always required.
 */
export function buildInspirationFilterSystemTagGroups(
  userFilters: InspirationFilterValue,
  lockedFilters?: Partial<InspirationFilterValue>
): string[][] {
  const groups: string[][] = [];
  const keys = Object.keys(userFilters) as (keyof InspirationFilterValue)[];

  for (const key of keys) {
    const prefix = FILTER_KEY_TAG_PREFIX[key];
    const lockedValues = lockedFilters?.[key] ?? [];
    const lockedSet = new Set<string>(lockedValues);

    // Locked values: their own mandatory group (always required).
    if (lockedValues.length > 0) {
      groups.push(lockedValues.map((value) => `${prefix}:${value}`));
    }

    // User values for this facet, excluding anything already covered by the
    // locked group so we don't emit a redundant identical group.
    const userValues = userFilters[key].filter((value) => !lockedSet.has(value));
    if (userValues.length > 0) {
      groups.push(userValues.map((value) => `${prefix}:${value}`));
    }
  }

  return groups;
}

/**
 * Read the demand-generation attributes stored on a content record.
 *
 * Returns the `data.marketing` object when Marketing Details is enabled, otherwise an
 * empty attribute set so callers can safely match/read facets without guarding
 * for undefined.
 */
export function getMarketingAttributesFromContent(
  content: Pick<ApiContent, 'data'>
): MarketingAttributes {
  const data = content.data as Record<string, unknown> | undefined;
  const enabled = data?.marketingEnabled === true;
  const marketing = (enabled ? (data?.marketing as Partial<MarketingAttributes>) : null) ?? {};

  return {
    marketingRoles: marketing.marketingRoles ?? [],
    journeyStages: marketing.journeyStages ?? [],
    primaryTransition: marketing.primaryTransition,
    productStages: marketing.productStages ?? [],
    marketingObjectives: marketing.marketingObjectives ?? [],
    tactics: marketing.tactics ?? [],
    channels: marketing.channels ?? [],
    deliveryDisciplines: marketing.deliveryDisciplines ?? [],
    deliveryActivities: marketing.deliveryActivities ?? [],
    collaborationModels: marketing.collaborationModels ?? [],
    activationFormats: marketing.activationFormats ?? [],
  };
}
